import { Color, Radius, Spacing } from 'zens-storybook'
import { StyleSheet, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { ApiError, CompanyData, DataWithUpload, Draft, useCompany, useStore } from 'zens-store'
import { Suspense, useEffect, useState } from 'react'
import { useRouter, useNumberParams } from 'zens-router'
import { usePermissions } from '@dms/hook'
import { CompanyHeader, CompanyOccupancyColorsForm, LayoutHeader } from '@dms/component'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    margin: Spacing.L,
    padding: Spacing.L,
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
  },
})

const CompanyOccupancyColors = () => {
  const router = useRouter()
  const { api, message } = useStore()
  const [{ companyId }] = useNumberParams()
  const company = useCompany({ companyId })
  const { isSysAdmin } = usePermissions(companyId)
  const [loading, setLoading] = useState(false)

  const { t } = useTranslation()

  useEffect(() => {
    if (!isSysAdmin) router.replace(`/companies/${companyId}/detail`)
  }, [companyId, isSysAdmin, router])

  const save = async (draft: Draft<DataWithUpload<CompanyData>>) => {
    if (!isSysAdmin || loading) return
    setLoading(true)
    try {
      await api.put(`companies/${companyId}/color-scheme`, { color_scheme: draft.data.color_scheme })
      await company.refetch()
      draft.settle()
      message.add({
        message: t('Occupancy colors were updated successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage } = error as ApiError
      message.add({
        message: `${t('Occupancy colors could not be saved')}. ${errorMessage}`,
        status: 'error',
      })
      return
    } finally {
      setLoading(false)
    }

    router.push(`/companies/${companyId}/detail`)
  }

  if (!isSysAdmin) return null

  return (
    <View style={styles.container}>
      <Suspense fallback={<LayoutHeader.Skeleton breadcrumb title />}>
        <CompanyHeader companyId={companyId} breadcrumbItems={[{ title: t('Occupancy colors') }]} />
      </Suspense>
      <View style={styles.content}>
        <Suspense fallback={<CompanyOccupancyColorsForm.Skeleton />}>
          <CompanyOccupancyColorsForm companyId={companyId} onSave={save} loading={loading} />
        </Suspense>
      </View>
    </View>
  )
}

export default CompanyOccupancyColors
